import dayjs from 'dayjs'
import { observer } from 'mobx-react-lite'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { OrderKeys, timetableGeneralOptions } from 'sections/LKSection/modules/OrderModule/data'
import { PickPointLocation } from 'sections/LKSection/modules/OrderModule/types'
import { TimetableModel } from 'sections/LKSection/shared/PickPointSharedModule/models/TimetableModel/TimetableModel'
import { PickPointType, WeekDays } from 'sections/LKSection/shared/PickPointSharedModule/types'
import { Info } from 'components/ui/Info/Info'
import { Checkbox, Flex, FormItem, Select, Text, TimeRangePicker } from 'components/uiKit'
import { TIME_FORMAT } from 'shared/constants/values'

type TimetableFormProps = {
  uiStore: TimetableModel
  pickPointType: PickPointType
  floorLevel: PickPointLocation
  disabled?: boolean
  hideInfo?: boolean
}

export const TimetableForm = observer(
  ({ uiStore, pickPointType, floorLevel, disabled = false, hideInfo }: TimetableFormProps) => {
    const { t } = useTranslation()

    const isFlexibleTimetable = pickPointType === PickPointType.folk || floorLevel === PickPointLocation.shoppingMall
    const style = { width: '100%' }
    const rangePickerStatus = uiStore.invalidOpeningHours ? 'error' : ''

    const dayOffChangeHandler = useCallback(
      (day: WeekDays, value: boolean) => {
        uiStore.setWeekDays(
          uiStore.weekDays.map((item) => ({
            ...item,
            isDayOff: item.label === day.label ? value : item.isDayOff,
          })),
        )
      },
      [uiStore.weekDays, uiStore.setWeekDays],
    )

    const openingHoursChangeHandler = useCallback(
      (open: string, close: string) => {
        uiStore.setTimetableStart(open)
        uiStore.setTimetableEnd(close)
      },
      [uiStore.setTimetableStart, uiStore.setTimetableEnd],
    )

    const getKey = (key: string) => `${OrderKeys.workConditionsStep}.${key}`

    return (
      <>
        {!hideInfo && (
          <Info>
            {isFlexibleTimetable ? (
              <Flex vertical>
                <Text>{t(getKey('Внимание! График работы'))}</Text>
                <Text>{t(getKey('Напоминаем, что оптимальное'))}</Text>
              </Flex>
            ) : (
              <Text>{t(getKey('Условия открытия'))}</Text>
            )}
          </Info>
        )}

        {isFlexibleTimetable ? (
          <>
            <FormItem label={t(getKey('Рабочие дни'))}>
              {uiStore.weekDays.map((day) => (
                <Flex key={day.label} vertical align="start">
                  <Checkbox
                    checked={!day.isDayOff}
                    onChange={(e) => dayOffChangeHandler(day, !e.target.checked)}
                    disabled={disabled}
                  >
                    {t(getKey(`weekDays.${day.label}`))}
                  </Checkbox>
                </Flex>
              ))}
            </FormItem>

            <FormItem label={t(getKey('Часы работы в рабочие дни'))}>
              <TimeRangePicker
                allowClear={false}
                status={rangePickerStatus}
                minuteStep={30}
                format={TIME_FORMAT}
                style={style}
                value={[dayjs(uiStore.timetableStart, TIME_FORMAT), dayjs(uiStore.timetableEnd, TIME_FORMAT)]}
                disabled={disabled}
                onChange={(e) => {
                  if (e?.[0] && e?.[1]) {
                    openingHoursChangeHandler(e[0].format(TIME_FORMAT), e[1].format(TIME_FORMAT))
                  }
                }}
              />
            </FormItem>
          </>
        ) : (
          <FormItem label={t(getKey('График работы с понедельника по воскресенье'))} style={style}>
            <Select
              value={uiStore.timetableGeneral}
              onChange={(value) => uiStore.setTimetableGeneral(value)}
              options={timetableGeneralOptions}
              disabled={disabled}
              placeholder={t(getKey('Выберите время работы'))}
            />
          </FormItem>
        )}
      </>
    )
  },
)
